import React, {useEffect, useMemo, useRef, useState} from 'react';
import {FiPause, FiPlay} from 'react-icons/fi';
import useMotionAutoplay from './useMotionAutoplay';

// The lesson uses the game's inline-playback recovery, with its own speed and
// box controls so practice does not change the player's saved game preferences.
export default function CitizenScienceTutorialClip({lesson, onReady, onRetry, active = true}) {
  const video = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);
  const [slow, setSlow] = useState(false);
  const [boxesShown, setBoxesShown] = useState(true);
  const [frame, setFrame] = useState(0);
  const {bindVideo, cancelAutoplay} = useMotionAutoplay(video, {enabled: true, mediaKey: lesson.videoUrl, active});
  const frames = useMemo(() => {
    const result = new Map();
    for (const box of lesson.boxes || []) {
      if (![box.frameIndex, box.x1, box.y1, box.x2, box.y2].every(Number.isFinite)) continue;
      if (!result.has(box.frameIndex)) result.set(box.frameIndex, []);
      result.get(box.frameIndex).push(box);
    }
    return result;
  }, [lesson.boxes]);
  useEffect(() => {
    if (!active || !playing || !boxesShown) return undefined;
    let animation;
    const update = () => {
      if (video.current) setFrame(Math.floor(video.current.currentTime * lesson.fps));
      animation = requestAnimationFrame(update);
    };
    update();
    return () => cancelAnimationFrame(animation);
  }, [active, playing, boxesShown, lesson.fps]);
  const play = () => {
    if (!active) return;
    cancelAutoplay();
    video.current?.play()?.catch(() => setPlaying(false));
  };
  const pause = () => { cancelAutoplay(); video.current?.pause(); };
  return <div className="cs-tutorial-media">
    <div className="cs-tutorial-video" style={{aspectRatio: `${lesson.frameW} / ${lesson.frameH}`}}>
      <video ref={bindVideo} src={lesson.videoUrl} aria-label="Practice clip" autoPlay={active} muted loop playsInline preload="auto"
        disablePictureInPicture onClick={playing ? pause : play}
        onLoadedData={() => { setFailed(false); onReady(); }} onError={() => { setFailed(true); onReady(false); }}
        onPlaying={() => setPlaying(true)} onPause={() => setPlaying(false)} />
      {boxesShown && <svg className="cs-tutorial-boxes" viewBox={`0 0 ${lesson.frameW} ${lesson.frameH}`} aria-hidden="true">
        {(frames.get(frame) || []).map((box, i) => {
          const x = Math.max(0, box.x1 - 4), y = Math.max(0, box.y1 - 4);
          return <rect key={i} x={x} y={y} width={Math.max(0, Math.min(lesson.frameW, box.x2 + 4) - x)} height={Math.max(0, Math.min(lesson.frameH, box.y2 + 4) - y)}
            fill="none" stroke={box.detected === false ? '#00c8ff' : '#ffff00'} strokeWidth="1" vectorEffect="non-scaling-stroke" />;
        })}
      </svg>}
      {!playing && !failed && <button type="button" className="cs-tutorial-play" onClick={play}><FiPlay aria-hidden="true" /> Play clip</button>}
      {failed && <div className="cs-tutorial-media-error" role="alert"><p>This clip couldn’t load.</p><button type="button" onClick={onRetry}>Reload clip</button></div>}
    </div>
    <div className="cs-tutorial-tools" role="group" aria-label="Practice video controls">
      <button type="button" disabled={failed} onClick={playing ? pause : play}>{playing ? <FiPause aria-hidden="true" /> : <FiPlay aria-hidden="true" />}{playing ? 'Pause' : 'Play'}</button>
      <button type="button" aria-pressed={slow} onClick={() => { video.current.playbackRate = slow ? 1 : 0.5; setSlow(!slow); }}>{slow ? '0.5×' : '1×'} speed</button>
      <button type="button" aria-pressed={boxesShown} onClick={() => setBoxesShown(!boxesShown)}>Boxes</button>
    </div>
  </div>;
}
